import { useState, useEffect, useCallback, useMemo } from 'react';
import { getGpsSessionPlayers, previewGpsEstimate, commitGpsEstimate, commitGpsManualValues, revertGpsEstimate } from '../../api';

const METRIC_LABEL = { td: 'Total Distance', rd: 'Running Distance', hid: 'High-Intensity Distance', sa: 'Accel+Decel', hmld: 'High Metabolic Load Distance' };
const METRICS = ['td', 'rd', 'hid', 'sa', 'hmld'];

// 0.8 is deliberately the same number as gpsEstimation.js's DURATION_HYGIENE_THRESHOLD — but the
// two answer different questions against different references, on purpose, and that's not
// something to "fix" into agreement later. This label asks "did this player train less than a
// normal full session" (reference: top-15-by-minutes median — the whole-squad median is itself
// dragged down by early leavers, so it's the wrong denominator for this question). The backend
// hygiene rule asks "is this row an outlier within its own session" (reference: that session's
// own median, self-excluded) — it decides squad-median membership, and every validated MdAPE/
// band was measured against THAT reference specifically, so it must not change. This label is
// advisory only; disagreement between the two for the same player on the same day is expected
// and acceptable, not a bug.
const DURATION_HYGIENE_THRESHOLD = 0.8;
const TOP_N_FOR_REFERENCE = 15;

function median(values) {
  const s = [...values].sort((a, b) => a - b);
  const mid = Math.floor(s.length / 2);
  return s.length % 2 ? s[mid] : (s[mid - 1] + s[mid]) / 2;
}

// Reached from the session view (GPSData.js) — pick a player, enter missed minutes, preview,
// confirm. Whole-session estimation is just missed_mins === totalMins, no separate code path.
export default function EstimateModal({ sessionId, user, onClose, onCommitted }) {
  const [rosterData, setRosterData] = useState(null);
  const [playerId, setPlayerId] = useState('');
  const [missedMins, setMissedMins] = useState('');
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [error, setError] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [manualMode, setManualMode] = useState(false);
  const [manualValues, setManualValues] = useState({});
  const [manualSaving, setManualSaving] = useState(false);
  const [revertConfirmOpen, setRevertConfirmOpen] = useState(false);
  const [reverting, setReverting] = useState(false);

  const refreshRoster = useCallback(() => {
    getGpsSessionPlayers(sessionId).then(setRosterData).catch(err => setError(err.message));
  }, [sessionId]);

  useEffect(() => { refreshRoster(); }, [refreshRoster]);

  const selectedPlayer = rosterData?.roster?.find(p => String(p.playerId) === String(playerId)) || null;

  // Leaving manual mode / switching player resets the draft — re-entering re-seeds from current
  // values fresh each time rather than carrying a stale draft across players.
  useEffect(() => { setManualMode(false); }, [playerId]);
  useEffect(() => {
    if (manualMode && selectedPlayer) {
      setManualValues({
        td: selectedPlayer.values?.td ?? 0, rd: selectedPlayer.values?.rd ?? 0, hid: selectedPlayer.values?.hid ?? 0,
        sa: selectedPlayer.values?.sa ?? 0, hmld: selectedPlayer.values?.hmld ?? 0,
      });
    }
  }, [manualMode, selectedPlayer]);

  // Top-15-by-minutes median, not the whole-session median — see the threshold comment above for
  // why. Falls back to null (no classification possible) when fewer than 15 players have data at
  // all, since "top 15 of 8" isn't a meaningful reference.
  const referenceMinutes = useMemo(() => {
    const roster = rosterData?.roster || [];
    const withData = roster.filter(p => p.hasData && p.values?.minutes != null).map(p => p.values.minutes);
    if (withData.length < TOP_N_FOR_REFERENCE) return null;
    const top15 = [...withData].sort((a, b) => b - a).slice(0, TOP_N_FOR_REFERENCE);
    return median(top15);
  }, [rosterData]);

  // Classifies every player into no-data / short / full, labels them, and sorts the dropdown
  // so the ones needing attention (no data, then shortest first) are at the top.
  const classifiedRoster = useMemo(() => {
    const roster = rosterData?.roster || [];
    const classified = roster.map(p => {
      if (!p.hasData) return { ...p, state: 'no-data', label: ' (no data)' };
      const mins = p.values.minutes;
      if (referenceMinutes != null && mins / referenceMinutes < DURATION_HYGIENE_THRESHOLD) {
        return { ...p, state: 'short', label: ` (short session — ${Math.round(mins)} of ${Math.round(referenceMinutes)} min)` };
      }
      return { ...p, state: 'full', label: '' };
    });
    const order = { 'no-data': 0, short: 1, full: 2 };
    classified.sort((a, b) => {
      if (order[a.state] !== order[b.state]) return order[a.state] - order[b.state];
      if (a.state === 'short') return a.values.minutes - b.values.minutes; // shortest first
      return a.name.localeCompare(b.name);
    });
    return classified;
  }, [rosterData, referenceMinutes]);

  // Defaults to full session length -> whole-session estimation, EXCEPT a short-session player
  // pre-fills to (reference - their actual) — almost always the right number, saves the mental
  // arithmetic of reading it off the label and subtracting by hand.
  useEffect(() => {
    if (!playerId) return;
    const p = classifiedRoster.find(x => String(x.playerId) === String(playerId));
    if (p?.state === 'short' && referenceMinutes != null) {
      setMissedMins(String(Math.round((referenceMinutes - p.values.minutes) * 100) / 100));
    } else if (rosterData?.totalMins != null) {
      setMissedMins(String(Math.round(rosterData.totalMins * 100) / 100));
    }
  }, [playerId, rosterData, classifiedRoster, referenceMinutes]);

  const runPreview = useCallback(() => {
    if (!playerId || missedMins === '' || isNaN(Number(missedMins))) { setPreview(null); return; }
    setLoading(true); setError(null);
    previewGpsEstimate(sessionId, playerId, Number(missedMins))
      .then(setPreview)
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, [sessionId, playerId, missedMins]);

  useEffect(() => { runPreview(); }, [runPreview]);

  const canCommit = preview && !preview.unimputable && !preview.error && ['staff', 'admin'].includes(user?.role);
  const disabledReason = !preview ? null
    : preview.unimputable ? `Unimputable — ${preview.reason === 'insufficient_reference_players' ? `only ${preview.referenceN} of ${preview.minRequired} required reference players trained that session` : preview.reason}`
    : preview.error ? preview.error
    : !['staff', 'admin'].includes(user?.role) ? 'Staff or admin access required'
    : null;

  const doCommit = async () => {
    setCommitting(true); setError(null);
    try {
      const result = await commitGpsEstimate(sessionId, playerId, Number(missedMins), user.id, user.role);
      if (result.error) throw new Error(result.error);
      setConfirmOpen(false);
      onCommitted && onCommitted(result);
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setCommitting(false);
    }
  };

  const doManualSave = async () => {
    setManualSaving(true); setError(null);
    try {
      const result = await commitGpsManualValues(sessionId, playerId, user.id, user.role, manualValues);
      if (result.error) throw new Error(result.error);
      setManualMode(false);
      refreshRoster();
      onCommitted && onCommitted(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setManualSaving(false);
    }
  };

  const doRevert = async () => {
    setReverting(true); setError(null);
    try {
      const result = await revertGpsEstimate(sessionId, playerId, user.id, user.role);
      if (result.error) throw new Error(result.error);
      setRevertConfirmOpen(false);
      refreshRoster();
      onCommitted && onCommitted(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setReverting(false);
    }
  };

  const overlayStyle = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 };
  const boxStyle = { background: 'var(--bg-primary, #fff)', color: 'var(--text-primary, #111)', borderRadius: 12, padding: 24, width: 560, maxWidth: '92vw', maxHeight: '88vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.35)' };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={boxStyle} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ margin: 0 }}>Estimate missed GPS data</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer' }}>×</button>
        </div>

        {error && <div style={{ color: '#c0392b', marginBottom: 12, fontSize: 13 }}>{error}</div>}

        <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Player</label>
        <select value={playerId} onChange={e => setPlayerId(e.target.value)} style={{ width: '100%', padding: 8, marginBottom: 12 }}>
          <option value="">Select a player…</option>
          {classifiedRoster.map(p => (
            <option key={p.playerId} value={p.playerId}>{p.name}{p.label}</option>
          ))}
        </select>

        {selectedPlayer?.isRehabThatDay && (
          <div style={{ background: '#fff3cd', border: '1px solid #ffe08a', borderRadius: 8, padding: 12, marginBottom: 12, fontSize: 13 }}>
            <strong>RTT/OFR:</strong> {selectedPlayer.name} was in rehab ({selectedPlayer.medicalStatus}) this session. If they did part of the session on schedule, that's complete data — estimating the rest fabricates load they never performed, inflates chronic load, and suppresses ACWR at exactly the wrong time for an RTP decision. Double-check this is a genuine equipment/tracking gap, not a rehab-scheduled partial session, before continuing.
          </div>
        )}

        {selectedPlayer?.isEstimated && (
          <div style={{ background: '#e0edff', border: '1px solid #b8d4ff', borderRadius: 8, padding: 12, marginBottom: 12, fontSize: 13 }}>
            <div style={{ marginBottom: 8 }}>
              <strong>Currently estimated</strong> ({selectedPlayer.estimatedMetrics.join(', ') || 'method: ' + selectedPlayer.estimationMethod}){selectedPlayer.estimatedAt && <> — {new Date(selectedPlayer.estimatedAt).toLocaleString()}</>}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => setRevertConfirmOpen(true)} style={{ padding: '6px 12px', fontSize: 12 }} disabled={!['staff', 'admin'].includes(user?.role)}>
                Revert to real data
              </button>
              <button onClick={() => setManualMode(m => !m)} style={{ padding: '6px 12px', fontSize: 12 }} disabled={!['staff', 'admin'].includes(user?.role)}>
                {manualMode ? 'Cancel manual edit' : 'Edit values manually'}
              </button>
            </div>
          </div>
        )}
        {selectedPlayer && !selectedPlayer.isEstimated && (
          <div style={{ marginBottom: 12 }}>
            <button onClick={() => setManualMode(m => !m)} style={{ padding: '6px 12px', fontSize: 12, background: 'none', border: '1px solid #ccc', borderRadius: 6, cursor: 'pointer' }} disabled={!['staff', 'admin'].includes(user?.role)}>
              {manualMode ? 'Cancel manual edit' : 'Enter values manually instead'}
            </button>
          </div>
        )}

        {manualMode ? (
          <div>
            <div style={{ fontSize: 12, color: '#888', marginBottom: 8 }}>
              Sets these as the player's total for this session directly — bypasses the ratio/team_median formula entirely. Still marked as estimated (method: manual), still reversible.
            </div>
            {METRICS.map(m => (
              <div key={m} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <label style={{ width: 180, fontSize: 13 }}>{METRIC_LABEL[m]}</label>
                <input type="number" step="any" value={manualValues[m] ?? ''}
                  onChange={e => setManualValues(v => ({ ...v, [m]: e.target.value === '' ? '' : Number(e.target.value) }))}
                  style={{ flex: 1, padding: 6 }} />
              </div>
            ))}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
              <button onClick={() => setManualMode(false)} style={{ padding: '8px 16px' }}>Cancel</button>
              <button onClick={doManualSave} disabled={manualSaving || !['staff', 'admin'].includes(user?.role)} style={{ padding: '8px 16px' }}>
                {manualSaving ? 'Saving…' : 'Save manual values'}
              </button>
            </div>
          </div>
        ) : (
        <>
        <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
          Missed minutes {rosterData?.totalMins != null && <span style={{ fontWeight: 400, color: '#888' }}>(session length: {rosterData.totalMins.toFixed(1)} min — defaults to full session)</span>}
        </label>
        <input type="number" min={0} max={rosterData?.totalMins || undefined} step={0.5} value={missedMins}
          onChange={e => setMissedMins(e.target.value)} style={{ width: '100%', padding: 8, marginBottom: 16 }} disabled={!playerId} />

        {loading && <div style={{ fontSize: 13, color: '#888' }}>Loading preview…</div>}

        {preview && !preview.unimputable && !preview.error && (
          <div>
            {preview.windowed && (
              <div style={{ fontSize: 12, color: '#888', marginBottom: 8 }}>Partial-window estimate — carries more uncertainty than a whole-session estimate; bands shown are the whole-session bands, not specific to this window.</div>
            )}
            <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ textAlign: 'left', borderBottom: '1px solid #ddd' }}>
                  <th style={{ padding: 6 }}>Metric</th><th style={{ padding: 6 }}>Real</th><th style={{ padding: 6 }}>Estimate (gap)</th><th style={{ padding: 6 }}>Total</th><th style={{ padding: 6 }}>Band</th><th style={{ padding: 6 }}>Method</th>
                </tr>
              </thead>
              <tbody>
                {METRICS.map(m => {
                  const v = preview.metrics[m];
                  if (!v || v.unimputable) return <tr key={m}><td style={{ padding: 6 }}>{METRIC_LABEL[m]}</td><td colSpan={5} style={{ padding: 6, color: '#888' }}>unimputable</td></tr>;
                  return (
                    <tr key={m} style={{ borderBottom: '1px solid #f0f0f0' }}>
                      <td style={{ padding: 6 }}>{METRIC_LABEL[m]}</td>
                      <td style={{ padding: 6 }}>{v.realValue.toFixed(0)}</td>
                      <td style={{ padding: 6 }}>{v.estimate.toFixed(0)}</td>
                      <td style={{ padding: 6, fontWeight: 600 }}>{v.total.toFixed(0)}</td>
                      <td style={{ padding: 6 }}>{v.bandP10.toFixed(0)}–{v.bandP90.toFixed(0)}</td>
                      <td style={{ padding: 6 }}>{v.method}{v.confidence === 'low' ? ' (low conf.)' : ''}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {preview?.unimputable && (
          <div style={{ fontSize: 13, color: '#c0392b', marginBottom: 12 }}>Unimputable: {preview.reason} (reference players: {preview.referenceN} of {preview.minRequired} required).</div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
          <button onClick={onClose} style={{ padding: '8px 16px' }}>Cancel</button>
          <span title={disabledReason || ''}>
            <button onClick={() => setConfirmOpen(true)} disabled={!canCommit} style={{ padding: '8px 16px', opacity: canCommit ? 1 : 0.5, cursor: canCommit ? 'pointer' : 'not-allowed' }}>
              Confirm estimate
            </button>
          </span>
        </div>
        </>
        )}

        {confirmOpen && (
          <div style={overlayStyle} onClick={() => setConfirmOpen(false)}>
            <div style={{ ...boxStyle, width: 420 }} onClick={e => e.stopPropagation()}>
              <h4 style={{ marginTop: 0 }}>Confirm estimate for {selectedPlayer?.name}?</h4>
              {selectedPlayer?.isRehabThatDay && (
                <div style={{ background: '#fff3cd', border: '1px solid #ffe08a', borderRadius: 8, padding: 12, marginBottom: 12, fontSize: 13 }}>
                  <strong>RTT/OFR warning:</strong> this player was in rehab this session. Confirm the missed portion is a real tracking gap, not scheduled rehab load reduction.
                </div>
              )}
              <p style={{ fontSize: 13 }}>This overwrites the stored values for this player/session and marks them as estimated. Original values are preserved in the provenance log.</p>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button onClick={() => setConfirmOpen(false)} style={{ padding: '8px 16px' }}>Cancel</button>
                <button onClick={doCommit} disabled={committing} style={{ padding: '8px 16px' }}>{committing ? 'Saving…' : 'Confirm'}</button>
              </div>
            </div>
          </div>
        )}

        {revertConfirmOpen && (
          <div style={overlayStyle} onClick={() => setRevertConfirmOpen(false)}>
            <div style={{ ...boxStyle, width: 420 }} onClick={e => e.stopPropagation()}>
              <h4 style={{ marginTop: 0 }}>Revert {selectedPlayer?.name}'s estimate?</h4>
              <p style={{ fontSize: 13 }}>Restores {selectedPlayer?.estimatedMetrics?.join(', ')} to the real value recorded before any estimate touched this row (or removes the row entirely if it never had real data). This cannot be undone from here — you'd need to re-run the estimate.</p>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button onClick={() => setRevertConfirmOpen(false)} style={{ padding: '8px 16px' }}>Cancel</button>
                <button onClick={doRevert} disabled={reverting} style={{ padding: '8px 16px' }}>{reverting ? 'Reverting…' : 'Revert'}</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
